import { useState } from "react";
import { Link } from "react-router-dom";
import type { AssetListItem, Visibility } from "../api/types";
import { formatDuration, monetizationLabel, relativeTime } from "../utils/format";
import { StatusBadge } from "./StatusBadge";
import { DurationOverlay } from "./DurationOverlay";
import { Thumbnail } from "./Thumbnail";
import { ConfirmDialog } from "./ConfirmDialog";
import { useT } from "../i18n";

const VIS_BADGE: Record<Visibility, { label: string; color: string }> = {
  public: { label: "Public", color: "bg-green-700" },
  private: { label: "Private", color: "bg-gray-600" },
  protected: { label: "Protected", color: "bg-yellow-700" },
};

function resolveTitle(asset: AssetListItem): string {
  return asset.resolvedTitle ?? asset.title;
}

export function VideoCard({
  asset,
  onDelete,
  onTagClick,
}: {
  asset: AssetListItem;
  onDelete?: (id: string, title: string) => void;
  onTagClick?: (tag: string) => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const displayTitle = resolveTitle(asset);
  const { t } = useT();

  return (
    <>
      <Link to={`/watch/${asset.id}`} className="group block w-full">
        <div className="relative aspect-video w-full rounded-xl overflow-hidden bg-brand-line">
          <Thumbnail url={asset.thumbnailUrl} alt={displayTitle} />
          <DurationOverlay seconds={asset.durationSeconds} />
          {/* Lock icon for non-public videos */}
          {asset.visibility !== "public" && (
            <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full flex items-center justify-center bg-black/70" title={asset.visibility}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z" />
              </svg>
            </div>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowConfirm(true);
              }}
              className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full flex items-center justify-center bg-black/70 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-colors"
              title="Delete video"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex gap-3 mt-3">
          <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center text-sm font-semibold shrink-0">
            {(asset.uploadedByEmail ?? "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium leading-snug line-clamp-2 group-hover:text-brand-text">
              {displayTitle}
            </h3>
            <div className="text-xs text-brand-muted mt-1 truncate">
              {asset.uploadedByEmail ?? t("common.unknown")}
            </div>
            <div className="flex items-center gap-2 text-xs text-brand-muted mt-0.5">
              <span>{relativeTime(asset.createdAt, t)}</span>
              <StatusBadge status={asset.status} />
              {asset.monetization !== "free" && (
                <span className="px-1 py-0.5 rounded text-[10px] bg-brand-line text-brand-muted">
                  {monetizationLabel(asset.monetization, t)}
                </span>
              )}
              {asset.visibility !== "public" && (
                <span className={`px-1 py-0.5 rounded text-[10px] text-white ${VIS_BADGE[asset.visibility]?.color ?? "bg-gray-600"}`}>
                  {t(`visibility.${asset.visibility}`)}
                </span>
              )}
            </div>
            <div className="text-[11px] text-brand-muted mt-0.5">
              {formatDuration(asset.durationSeconds)}
            </div>
            {asset.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {asset.tags.slice(0, 3).map((tag) => (
                  <button
                    key={tag}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onTagClick?.(tag);
                    }}
                    className="px-1.5 py-0.5 rounded text-[10px] bg-brand-line text-brand-muted hover:bg-brand-hover hover:text-brand-text transition-colors"
                  >
                    {tag}
                  </button>
                ))}
                {asset.tags.length > 3 && (
                  <span className="px-1 py-0.5 text-[10px] text-brand-muted">+{asset.tags.length - 3}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </Link>

      {onDelete && (
        <ConfirmDialog
          open={showConfirm}
          title={t("watch.deleteTitle")}
          message={t("watch.deleteMessage", { title: displayTitle })}
          onConfirm={() => {
            setShowConfirm(false);
            onDelete(asset.id, asset.title);
          }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
}
